import React, { useState, useContext } from "react";
import styled from "styled-components";
import { parseTimeEvolution } from "views/Statistics/DPI/ApplicationMetrics/api";
import DistributionPlot from "common/graphs/DistributionPlot";
import Switch from "@mui/material/Switch";
import Loading from "common/Loading";
import * as d3 from "d3";
import BqnContext from "common/BqnContext";
import {
  PERIOD_DASHBOARD_TO_HOURS,
  LOAD_INTERVAL,
  DPI_STATS_DASHBOARD_TMOUT_MS,
  appMetricsTimeInterval,
} from "./utils";
import useDashboardData from "./useDashboardData";
import { atBqnTarget } from "common/api";

const Wrapper = styled.div`
  width: 100%;
`;

const Subtitle = styled.div`
  font-size: 12px !important;
  color: var(--text-primary);
  margin-top: 10px;
`;

const LayoutRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 750px) {
    flex-direction: column;
    align-items: flex-end;
    flex-wrap: wrap;

    .displaySelect {
      margin-right: 30px;
    }
  }
`;

const SwitchWrapper = styled.div`
  width: auto;
  display: flex;
  align-items: center;
  position: relative;
`;

const SwitchWithLabel = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

const Label = styled.div`
  margin-right: 10px;
  font-family: AllianceNo2-SemiBold !important;
  font-weight: 100;
  color: var(--Text-Primary);
  width: 100px;
  text-align: right;
`;

const WrapperLoader = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 350px;
  color: var(--text-primary);
`;

// The interval asked is the one that aggregates the period into the few
// points the chart draws, instead of one point per hour: the BQN computes the
// percentiles of the wider interval, so no sample is left out of the boxes.
const getApplicationMetricsTimeData = ({hours, mode, bqnId}) => {
  const interval = appMetricsTimeInterval(hours);

  return ifCl.run(
    atBqnTarget(
      `show statistics dpi app-metrics hours ${hours} interval ${interval} category all subscriber all policy all subscriber-group all`,
      mode,
      bqnId
    ),
    { timeout: DPI_STATS_DASHBOARD_TMOUT_MS }
  )
};

// A BQN that is offline answers nothing, and one that is not among the ones
// the cluster reports is not there to answer: neither of them is asked.
const isBqnOnline = (mode, bqnId, bqns) => {
  const bqnsOnline = bqns.reduce((acc, bqn) => {
    acc[bqn.bqnId] = bqn.function;
    return acc;
  }, {});

  return !(
    (mode === "cluster" && bqnId !== "all" && !bqnsOnline[bqnId]) ||
    bqnsOnline[bqnId] === "offline"
  );
};

function ApplicationMetricsTime({ period }) {
  const { bqns } = useContext(BqnContext);

  const [plotMode, setPlotMode] = useState("boxplot");
  const [showMinMax, setShowMinMax] = useState(false);

  // The chart has a cadence of its own: it draws no range of the dashboard,
  // so nothing else asks for its data again.
  const { data, isLoading } = useDashboardData({
    retrieve: ({ mode, bqnId }) =>
      isBqnOnline(mode, bqnId, bqns)
        ? getApplicationMetricsTimeData({
            hours: PERIOD_DASHBOARD_TO_HOURS[period],
            mode,
            bqnId,
          }).then((response) => parseTimeEvolution(response).downlink)
        : Promise.resolve(null),
    onError: "Error fetching application metrics time data:",
    deps: [period, bqns],
    interval: LOAD_INTERVAL,
  });

  if (isLoading) {
    return (
      <WrapperLoader>
        <Loading />
      </WrapperLoader>
    );
  }

  if (!data || data.length === 0) {
    return <WrapperLoader>No data available</WrapperLoader>;
  }

  return (
    <Wrapper>
      <Subtitle>Downlink</Subtitle>
      <LayoutRow>
        <SwitchWrapper>
          <SwitchWithLabel>
            <Label>Line chart view</Label>{" "}
            <Switch
              className="sw-input"
              checked={plotMode !== "boxplot"}
              onChange={() =>
                setPlotMode(plotMode === "boxplot" ? "line" : "boxplot")
              }
            />
          </SwitchWithLabel>
          <SwitchWithLabel>
            <Label>View max+min</Label>{" "}
            <Switch
              className="sw-input"
              checked={showMinMax}
              onChange={() => setShowMinMax(!showMinMax)}
            />
          </SwitchWithLabel>
        </SwitchWrapper>
      </LayoutRow>
      <DistributionPlot
        plotMode={plotMode}
        data={data}
        xField="time"
        formatXField={d3.timeFormat("%m/%d %H:%M")}
        minmax={showMinMax}
        yAxisUnit="Mbps"
        margin={{ top: 10, right: 50, bottom: 70, left: 60 }}
      />
    </Wrapper>
  );
}

export default React.memo(ApplicationMetricsTime);
